import { useState, useMemo } from 'react'
import { type LoginCredentials, type SignupCredentials } from '../types'
import { Link } from '@tanstack/react-router'

interface AuthCardProps {
  type: 'signin' | 'signup'
  title: string
  subtitle: string
  onSubmit: (data: LoginCredentials | SignupCredentials) => void
  onGoogleAuth: () => void
  isLoading?: boolean
  error?: string
}

const AuthCard = ({ 
  type, 
  title, 
  subtitle, 
  onSubmit, 
  onGoogleAuth, 
  isLoading = false,
  error 
}: AuthCardProps) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    rememberMe: false
  })

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    name?: string;
    general?: string;
  }>({})

  const [touched, setTouched] = useState<{
    email?: boolean;
    password?: boolean;
    confirmPassword?: boolean;
    name?: boolean;
  }>({})

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  // Password requirements validation (only for signup)
  const passwordRequirements = useMemo(() => {
    if (type !== 'signup') return null
    
    return {
      minLength: formData.password.length >= 8,
      hasUppercase: /[A-Z]/.test(formData.password),
      hasLowercase: /[a-z]/.test(formData.password),
      hasNumber: /\d/.test(formData.password),
      hasSpecial: /[@$!%*?&]/.test(formData.password),
    }
  }, [formData.password, type])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    const newErrors: typeof errors = {}
    let isValid = true

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.'
      isValid = false
    }

    // Password validation
    if (type === 'signup') {
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long.'
        isValid = false
      } else if (formData.password.length > 128) {
        newErrors.password = 'Password cannot exceed 128 characters.'
        isValid = false
      } else {
        const passwordComplexityRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/
        if (!passwordComplexityRegex.test(formData.password)) {
          newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&).'
          isValid = false
        }
      }
    }

    if (type === 'signup') {
      // Password match validation
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match.'
        isValid = false
      }
    }

    if (!isValid) {
      setErrors(newErrors)
      return
    }

    if (type === 'signup') {
      onSubmit({
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        name: formData.name,
      } as SignupCredentials)
    } else {
      onSubmit({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
      } as LoginCredentials)
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Perform real-time validation for touched fields
    if (touched[field as keyof typeof touched] && typeof value === 'string') {
      validateField(field, value)
    }
  }

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }))
    
    // Validate on blur
    const value = formData[field as keyof typeof formData]
    if (typeof value === 'string') {
      validateField(field, value)
    }
  }

  const validateField = (field: string, value: string) => {
    const newErrors: typeof errors = { ...errors }

    switch (field) {
      case 'name':
        if (isSignup) {
          if (!value.trim()) {
            newErrors.name = 'Name is required.'
          } else if (value.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters.'
          } else if (!/^[a-zA-Z\s]+$/.test(value)) {
            newErrors.name = 'Name should only contain letters and spaces.'
          } else {
            delete newErrors.name
          }
        }
        break

      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!value) {
          newErrors.email = 'Email is required.'
        } else if (!emailRegex.test(value)) {
          newErrors.email = 'Please enter a valid email address.'
        } else {
          delete newErrors.email
        }
        break
      }

      case 'confirmPassword':
        if (isSignup) {
          if (!value) {
            newErrors.confirmPassword = 'Please confirm your password.'
          } else if (value !== formData.password) {
            newErrors.confirmPassword = 'Passwords do not match.'
          } else {
            delete newErrors.confirmPassword
          }
        }
        break
    }

    setErrors(newErrors)
  }

  const isSignup = type === 'signup'

  // Derived errors from props (API) + state (Local)
  const apiEmailError = error && error.includes('User with this email already exists') ? error : undefined;
  
  // For login invalid credentials, we want to highlight both fields but maybe only show message on one or simply header
  // User requested: "show errors on both email and password"
  const isInvalidCredentials = error && (error.includes('Invalid email or password') || error.includes('Authentication required'));

  // Combine local and API errors
  const emailError = errors.email || apiEmailError || (isInvalidCredentials ? ' ' : undefined); // Space to trigger red border but no double message if we show it globally or elsewhere
  const passwordError = errors.password || (isInvalidCredentials ? ' ' : undefined);
  const confirmPasswordError = errors.confirmPassword;

  // Global error is shown only if NOT consumed by a field EXCLUDING invalid credentials which we might want to show globally AND highlight fields, 
  // OR just highlight fields. Usually for invalid creds, we show a global alert "Invalid email or password" and highlight inputs.
  // Let's keep the global alert for invalid credentials AND highlight the fields (by setting emailError/passwordError to non-null).
  const displayGlobalError = errors.general || (!apiEmailError ? error : undefined);

  const isFormValid = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isEmailValid = emailRegex.test(formData.email)

    if (isSignup) {
      const isNameValid = formData.name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(formData.name)
      const isPasswordValid = passwordRequirements ? Object.values(passwordRequirements).every(Boolean) : false
      const isConfirmValid = formData.password === formData.confirmPassword && formData.confirmPassword !== ''

      return isNameValid && isEmailValid && isPasswordValid && isConfirmValid
    }

    return isEmailValid && formData.password !== ''
  }, [formData, isSignup, passwordRequirements])

  return (
    <div className="min-h-[90dvh] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {title}
            </h2>
            <p className="text-gray-600">
              {subtitle}
            </p>
          </div>

          {displayGlobalError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{displayGlobalError}</p>
            </div>
          )}

          {/* Google Auth Button */}
          <button
            onClick={onGoogleAuth}
            disabled={isLoading}
            className="w-full flex justify-center items-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">OR</span>
            </div>
          </div>

          {/* Auth Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {isSignup && (
              <div>
                <label htmlFor="name" className="block w-fit text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  placeholder="John Doe"
                  className={`appearance-none block w-full px-3 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block w-fit text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                placeholder="you@example.com"
                className={`appearance-none block w-full px-3 py-3 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
              />
              {emailError && emailError.trim() !== '' && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
            </div>

            <div className="relative">
              <label htmlFor="password" className="block w-fit text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  required
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onFocus={() => isSignup && setIsPasswordFocused(true)}
                  onBlur={() => setIsPasswordFocused(false)}
                  placeholder="••••••••"
                  className={`appearance-none block w-full px-3 py-3 pr-10 border ${passwordError ? 'border-red-500' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && passwordError.trim() !== '' && <p className="mt-1 text-sm text-red-600">{passwordError}</p>}

              {/* Password Requirements Checklist - Only for Signup */}
              {isSignup && isPasswordFocused && passwordRequirements && (
                <div className="absolute left-0 right-0 top-full mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-xl z-50 space-y-2">
                  <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center text-xs">
                      <svg 
                        className={`h-4 w-4 mr-2 ${passwordRequirements.minLength ? 'text-green-500' : 'text-red-500'}`} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        {passwordRequirements.minLength ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        )}
                      </svg>
                      <span className={passwordRequirements.minLength ? 'text-green-700' : 'text-red-700'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <svg 
                        className={`h-4 w-4 mr-2 ${passwordRequirements.hasUppercase ? 'text-green-500' : 'text-red-500'}`} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        {passwordRequirements.hasUppercase ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        )}
                      </svg>
                      <span className={passwordRequirements.hasUppercase ? 'text-green-700' : 'text-red-700'}>
                        One uppercase letter (A-Z)
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <svg 
                        className={`h-4 w-4 mr-2 ${passwordRequirements.hasLowercase ? 'text-green-500' : 'text-red-500'}`} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        {passwordRequirements.hasLowercase ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        )}
                      </svg>
                      <span className={passwordRequirements.hasLowercase ? 'text-green-700' : 'text-red-700'}>
                        One lowercase letter (a-z)
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <svg 
                        className={`h-4 w-4 mr-2 ${passwordRequirements.hasNumber ? 'text-green-500' : 'text-red-500'}`} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        {passwordRequirements.hasNumber ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        )}
                      </svg>
                      <span className={passwordRequirements.hasNumber ? 'text-green-700' : 'text-red-700'}>
                        One number (0-9)
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <svg 
                        className={`h-4 w-4 mr-2 ${passwordRequirements.hasSpecial ? 'text-green-500' : 'text-red-500'}`} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        {passwordRequirements.hasSpecial ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        )}
                      </svg>
                      <span className={passwordRequirements.hasSpecial ? 'text-green-700' : 'text-red-700'}>
                        One special character (@$!%*?&)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isSignup && (
              <div>
                <label htmlFor="confirmPassword" className="block w-fit text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    onBlur={() => handleBlur('confirmPassword')}
                    placeholder="••••••••"
                    className={`appearance-none block w-full px-3 py-3 pr-10 border ${confirmPasswordError ? 'border-red-500' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {confirmPasswordError && <p className="mt-1 text-sm text-red-600">{confirmPasswordError}</p>}
              </div>
            )}

            {!isSignup && (
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                  className="h-4 w-4 text-[#5761EB] focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block w-fit text-sm text-gray-700">
                  Remember me
                </label>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#5761EB] hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isSignup ? 'Creating Account...' : 'Signing In...'}
                  </div>
                ) : (
                  isSignup ? 'Create Account' : 'Sign In'
                )}
              </button>
            </div>
          </form>

          {/* Footer Links */}
          <div className="mt-6 flex justify-between text-sm">
            {isSignup ? (
              <Link to="/sign-in" className="text-[#5761EB] hover:text-indigo-500">
                Already have an account? Sign in
              </Link>
            ) : (
              <>
                <Link to="/sign-up" className="text-[#5761EB] hover:text-indigo-500">
                  Create account
                </Link>
                <Link to="/forgot-password" className="text-[#5761EB] hover:text-indigo-500">
                  Forgot password?
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthCard